import { useEffect, useState } from "react";
import { Copy, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, slDate, slDateTime } from "@/lib/api";

const SERVICES = [["extra_year", "Dodatno leto hrambe", "19 €"], ["extra_storage", "Dodaten prostor", "po dogovoru"], ["collage", "Foto kolaž", "39 €"]];

export default function PlanPanel({ wedding }) {
  const [reqs, setReqs] = useState([]);
  const [invite, setInvite] = useState(null);
  const s = wedding.stats;
  const pct = Math.min(100, (s.storage_used / wedding.storage_quota_bytes) * 100);
  const load = () => api.get(`/manage/weddings/${wedding.id}/requests`).then((r) => setReqs(r.data));
  useEffect(() => { load(); }, [wedding.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const request = async (type) => {
    const note = type === "deletion" ? window.prompt("Zahteva za predčasni izbris vseh podatkov poroke. Po potrditvi izbrisa postopka ni mogoče razveljaviti. Razlog (neobvezno):") : window.prompt("Dodatna navodila ali želje (neobvezno):", "");
    if (note === null) return;
    try {
      await api.post(`/manage/weddings/${wedding.id}/requests`, { type, note });
      toast.success("Zahteva je poslana. Kontaktirali vas bomo s podatki za plačilo oziroma potrditvijo.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const makeInvite = async () => {
    const r = await api.post(`/manage/weddings/${wedding.id}/invite`);
    setInvite(r.data.url);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="plan-panel">
      <div className="card-lux p-6 space-y-5">
        <h3 className="font-display text-2xl">Paket {wedding.package_name}</h3>
        <div>
          <div className="flex justify-between text-sm mb-2"><span>Porabljen prostor</span><span data-testid="plan-storage-used">{fmtBytes(s.storage_used)} od {fmtBytes(wedding.storage_quota_bytes)}</span></div>
          <div className="h-2 rounded-full bg-[#EADBCE] overflow-hidden"><div className="h-full bg-[#C5A880]" style={{ width: `${pct}%` }} /></div>
        </div>
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <div><dt className="text-[#6C6560]">Fotografije</dt><dd className="font-display text-2xl">{s.photos}</dd></div>
          <div><dt className="text-[#6C6560]">Videi</dt><dd className="font-display text-2xl">{s.videos}</dd></div>
          <div><dt className="text-[#6C6560]">Začetek hrambe</dt><dd data-testid="plan-storage-start">{wedding.storage_start ? slDate(wedding.storage_start) : "ob aktivaciji"}</dd></div>
          <div><dt className="text-[#6C6560]">Hramba do</dt><dd className="font-medium" data-testid="plan-expires-at">{wedding.expires_at ? slDate(wedding.expires_at) : "365 dni po aktivaciji"}</dd></div>
        </dl>
        <p className="text-xs text-[#6C6560] leading-relaxed">Hramba traja 365 dni od dneva poroke (oz. od aktivacije, če je ta po poroki). Po izteku sledi 30-dnevno obdobje odloga, ko je prenos še mogoč, nato se podatki trajno izbrišejo.</p>
        <div className="border-t border-[#EADBCE] pt-5 space-y-3">
          <p className="text-sm font-medium">Upravljavci poroke</p>
          <ul className="text-sm text-[#6C6560]">{wedding.managers.map((m) => <li key={m}>{m}</li>)}</ul>
          <button onClick={makeInvite} className="btn-line px-5 py-2.5 text-sm flex items-center gap-2" data-testid="plan-invite-partner"><UserPlus size={16} /> Povabi partnerja</button>
          {invite && (
            <div className="flex items-center gap-2 bg-[#F3ECE1] rounded-xl p-3" data-testid="plan-invite-link">
              <code className="text-xs flex-1 truncate">{invite}</code>
              <button onClick={() => { navigator.clipboard.writeText(invite); toast.success("Povezava je kopirana."); }} className="p-2 rounded-lg hover:bg-white" aria-label="Kopiraj"><Copy size={16} /></button>
            </div>
          )}
          {invite && <p className="text-xs text-[#968E87]">Pošljite povezavo partnerju. Veljavna je 7 dni.</p>}
        </div>
      </div>
      <div className="space-y-6">
        <div className="card-lux p-6 space-y-3">
          <h3 className="font-display text-2xl">Dodatne storitve</h3>
          {SERVICES.map(([k, l, p]) => (
            <div key={k} className="flex items-center justify-between gap-3 py-2 border-b border-[#EADBCE]/60 last:border-0">
              <div><p className="text-sm font-medium">{l}</p><p className="text-xs text-[#6C6560]">{p}</p></div>
              <button onClick={() => request(k)} className="btn-line px-4 py-1.5 text-xs" data-testid={`plan-request-${k}`}>Naroči</button>
            </div>
          ))}
          <p className="text-xs text-[#968E87]">Po oddaji zahteve vas kontaktiramo s podatki za plačilo.</p>
        </div>
        <div className="card-lux p-6 space-y-3">
          <h3 className="font-display text-2xl">Zahteve</h3>
          {reqs.length === 0 ? <p className="text-sm text-[#6C6560]">Ni oddanih zahtev.</p> : reqs.map((r) => (
            <div key={r.id} className="text-sm flex justify-between gap-3" data-testid="plan-request-row"><span>{r.label}</span><span className="text-[#6C6560]">{r.status === "odprto" ? "V obdelavi" : "Zaključeno"} · {slDateTime(r.created_at)}</span></div>
          ))}
          <button onClick={() => request("deletion")} className="text-xs underline text-red-800 pt-2" data-testid="plan-request-deletion">Zahtevaj predčasni izbris podatkov</button>
        </div>
      </div>
    </div>
  );
}
